import { Box, ListItem, ListItemIcon, ListItemText, Typography } from "@mui/material";

import BaseEntitySearchAutocomplete, {
  BaseEntitySearchAutocompleteInheritableProps,
} from "@/common/components/Entity/components/BaseEntitySearchAutocomplete";
import AppIcon from "@/common/components/Icons/AppIcon";
import { renderIf } from "@/common/helpers/render/renderIf";
import { StringHelper } from "@/common/helpers/string";
import { AutocompleteOptionType, BaseAutocompleteOption } from "@/common/ts/autocomplete";
import { apiClient } from "@/core/api/ApiClient";
import { CustomTagDto, CustomTagSearchPaginatedDto } from "@/core/api/generated/v1";

export type CustomTagAutocompleteOption = BaseAutocompleteOption<CustomTagDto>;

export const getCustomTagOptionTitle = (data: CustomTagDto): string =>
  StringHelper.join([data.name], " | ", {
    skipEmpty: true,
  });

export const customTagToOption = (data: CustomTagDto): CustomTagAutocompleteOption => ({
  id: data.id!,
  title: getCustomTagOptionTitle(data),
  optionType: AutocompleteOptionType.Normal,
  data: data,
});

export interface CustomTagAutocompleteProps extends BaseEntitySearchAutocompleteInheritableProps<CustomTagDto> {
  searchFilters?: Partial<Omit<CustomTagSearchPaginatedDto, "search" | "includeIds">>;
}

export default function CustomTagAutocomplete({
  searchFilters,

  isPreload = true,
  ...otherProps
}: CustomTagAutocompleteProps) {
  return (
    <BaseEntitySearchAutocomplete
      sx={{ minWidth: 200, flex: 1 }}
      entityToOption={customTagToOption}
      isPreload={isPreload}
      request={{
        requestFunc: apiClient.customTagsApi.apiV1TagsCustomSearchPost,
        limit: 25,
        parameters: {
          nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
          customTagSearchPaginatedDto: {
            ...searchFilters,
          },
        },
        combineParameters: (params, newParams) => ({
          ...params,
          customTagSearchPaginatedDto: {
            ...params.customTagSearchPaginatedDto,
            ...newParams,
          },
        }),
        deps: [searchFilters],
      }}
      label='Custom tag'
      placeholder='Search for custom tag...'
      renderOption={(props, option) => {
        return (
          <ListItem {...props}>
            <ListItemIcon>
              {renderIf()
                .if(option.optionType === AutocompleteOptionType.Normal)
                .then(<AppIcon of='customTag' />)
                .elseif(option.optionType === AutocompleteOptionType.DynamicCreateNew)
                .then(<AppIcon of='add' />)
                .render()}
            </ListItemIcon>
            <ListItemText
              primary={
                <>
                  <Typography
                    variant={
                      option.optionType === AutocompleteOptionType.DynamicCreateNew
                        ? "subtitle1"
                        : "body1"
                    }
                    component='div'
                  >
                    {option.title}
                  </Typography>
                </>
              }
              secondary={
                option.data && (
                  <>
                    <Box>
                      <Typography component='div' variant='body2'>
                        {option.data?.description}
                      </Typography>
                    </Box>
                  </>
                )
              }
            />
          </ListItem>
        );
      }}
      {...otherProps}
    />
  );
}
